import React from 'react';
import { defineMessages } from 'react-intl';
import { SaveOutlined, StopOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DEFAULT_TABLE_ITEM_SIZE, EMPTY_DETAIL } from '@constants';
import apiConfig from '@constants/apiConfig';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { Button, Flex } from 'antd';

import ServerProviderForm from './ServerProviderForm';

const message = defineMessages({
    objectName: 'Server Provider',
});

const FORM_ID = 'form-server-provider';

const ServerProviderListPage = () => {
    const translate = useTranslate();

    const {
        data,
        mixinFuncs,
        queryFilter,
        pagination,
        loading,
        detail,
        loadingSave,
        openModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        isEditing,
        isChanged,
        titleModal,
    } = useListModal({
        apiConfig: {
            getList: apiConfig.serverProvider.getList,
            getById: apiConfig.serverProvider.getById,
            create: apiConfig.serverProvider.create,
            update: apiConfig.serverProvider.update,
            delete: apiConfig.serverProvider.delete,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
    });

    const columns = [
        {
            title: translate.formatMessage(commonMessage.name),
            dataIndex: 'name',
        },
        {
            title: translate.formatMessage(commonMessage.apiUrl),
            dataIndex: 'url',
        },
        {
            title: translate.formatMessage(commonMessage.max),
            dataIndex: 'maxTenant',
            align: 'center',
            width: '100px',
        },
        {
            title: translate.formatMessage(commonMessage.count),
            dataIndex: 'currentTenantCount',
            align: 'center',
            width: '100px',
        },
        {
            title: translate.formatMessage(commonMessage.serverId),
            dataIndex: 'serverId',
            width: '120px',
        },
        mixinFuncs.renderActionColumn(
            {
                edit: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ];

    const searchFields = [
        {
            key: 'name',
            placeholder: translate.formatMessage(commonMessage.name),
        },
        {
            key: 'driverClassName',
            placeholder: translate.formatMessage(commonMessage.driverClassName),
        },
    ];

    return (
        <PageWrapper routes={[{ breadcrumbName: translate.formatMessage(commonMessage.serverProvider) }]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                actionBar={mixinFuncs.renderActionBar()}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        loading={loading}
                    />
                }
            />
            {openModal && (
                <BaseModal
                    isModalVisible={openModal}
                    isEditing={isEditing}
                    handleCancel={onCancelModal}
                    title={titleModal}
                    width="800px"
                    noBodyPadding={true}
                    customFooter={
                        <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
                            <Button danger onClick={onCancelModal} icon={<StopOutlined />}>
                                {translate.formatMessage(commonMessage.cancel)}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                form={FORM_ID}
                                loading={loadingSave}
                                disabled={isEditing && !isChanged}
                                icon={<SaveOutlined />}
                            >
                                {translate.formatMessage(isEditing ? commonMessage.update : commonMessage.save)}
                            </Button>
                        </Flex>
                    }
                >
                    <ServerProviderForm
                        formId={FORM_ID}
                        dataDetail={detail || EMPTY_DETAIL}
                        isEditing={isEditing}
                        actions={null}
                        onSubmit={onSave}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default ServerProviderListPage;
